import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {minimalEditorConfig} from '../common/html-editor.config';
import {FileUploadService} from '../services/file-upload.service';
import {Observable, throwError} from 'rxjs';
import {TenantService} from '../services/tenant.service';
import {catchError, concatMap, last, map, tap} from 'rxjs/operators';
import {HomePageContent} from '../models/content/home-page-content.model';
import {ContentDbService} from '../services/content-db.service';
import {AppState} from '../store';
import {select, Store} from '@ngrx/store';
import {selectContent} from '../store/content.selectors';
import {deepClone} from '../common/collection-utils';
import {homePageContentUpdated} from '../store/content.actions';
import {MessagesService} from '../shared/services/messages.service';
import {MatButtonModule} from '@angular/material/button';
import {ColorFieldComponent} from '../shared/color-field/color-field.component';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {FileUploadProgressComponent} from '../shared/file-upload-progress/file-upload-progress.component';
import {FileUploadComponent} from '../shared/file-upload/file-upload.component';
import {MatTabsModule} from '@angular/material/tabs';
import {LoadingComponent} from '../shared/loading/loading.component';
import {MessagesComponent} from '../shared/messages/messages.component';
import {AsyncPipe, NgIf} from '@angular/common';
import { UploadTask } from '@angular/fire/storage';

@Component({
    selector: 'edit-home-dialog',
    templateUrl: './edit-home-dialog.component.html',
    styleUrls: ['./edit-home-dialog.component.scss'],
    providers: [
        MessagesService
    ],
    standalone: true,
    imports: [NgIf, MatDialogModule, MessagesComponent, LoadingComponent, MatTabsModule, FileUploadComponent, FileUploadProgressComponent, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, ColorFieldComponent, MatButtonModule, AsyncPipe]
})
export class EditHomeDialogComponent implements OnInit {

  form: FormGroup;

  titleColor = '#ffffff';

  homePageContent$: Observable<HomePageContent>;

  editorConfig = minimalEditorConfig;

  bannerUploadTask: UploadTask;
  bannerPercentageUpload$: Observable<number>;

  logoUploadTask: UploadTask;
  logoPercentageUpload$: Observable<number>;


  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<EditHomeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data,
    private upload: FileUploadService,
    private tenant: TenantService,
    private messages: MessagesService,
    private contentDb: ContentDbService,
    private store: Store<AppState>) {

    this.form = fb.group({
      title: [''],
      titleFontSize: [30]
    })

  }

  ngOnInit() {

    this.homePageContent$ = this.store.pipe(select(selectContent('homePage')));

    this.homePageContent$
      .subscribe(content => {

        this.titleColor = content.pageTitleColor;

        if (!content.pageTitle.startsWith("Click the Edit Header")) {
          this.form.patchValue({
            title:content.pageTitle,
            titleFontSize: content.titleFontSize
          });
        }

      });

  }

  close() {
    this.dialogRef.close();
  }


  onBannerSelected(event, content: HomePageContent) {

    const imageFile = event.target.files[0];

    if (imageFile) {

      const filePath = this.imageBasePath() + '/' + imageFile.name;

      this.bannerUploadTask = this.upload.uploadFile(imageFile, this.imageBasePath());
      console.log("Banner upload task: ", this.bannerUploadTask)
      this.bannerPercentageUpload$ = this.upload.percentageChanges(this.bannerUploadTask);

      this.uploadImage(this.bannerUploadTask, filePath, "banner", imageFile.name, content)
        .subscribe();

    }

  }


  onLogoSelected(event, content: HomePageContent) {

    const imageFile = event.target.files[0];

    if (imageFile) {

      const filePath = this.imageBasePath() + '/' + imageFile.name;

      this.logoUploadTask = this.upload.uploadFile(imageFile, this.imageBasePath());
      this.logoPercentageUpload$ = this.upload.percentageChanges(this.logoUploadTask);
      this.uploadImage(this.logoUploadTask, filePath, "logo", imageFile.name, content)
        .subscribe();

    }

  }

  uploadImage(uploadTask: UploadTask, filePath: string, imageId: string, fileName:string, content: HomePageContent):Observable<any> {
    return this.upload.uploadTaskToObservable(uploadTask)
      .pipe(
        last(),
        catchError(err => {
          console.log("Error uploading image, reason: ", err);
          this.messages.error('Could not upload image. File size must be less than 500KB');
          return throwError(err);
        }),
        tap(() => this.messages.info('Image uploaded, applying it now.')),
        concatMap(() => this.upload.getDownloadUrl(filePath)),
        tap(url => {

          const newContent: HomePageContent = deepClone(content);

          newContent[imageId + 'ImageUrl'] = url;
          newContent[imageId + 'FileName'] = fileName;

          this.store.dispatch(homePageContentUpdated({content:newContent}));
        })
      );

  }

  onBannerCancelUpload() {
    this.bannerUploadTask.cancel();
    this.bannerUploadTask = null;
    this.bannerPercentageUpload$ = null;
  }

  onLogoCancelUpload() {
    this.logoUploadTask.cancel();
    this.logoUploadTask = null;
    this.logoPercentageUpload$ = null;
  }

  imageBasePath() {
    return `${this.tenant.id}/content/home`;
  }

  onImageDeleted(imageKey:string, content:HomePageContent) {

    const imageUrlProperty = imageKey + 'ImageUrl',
          imageFileNameProperty = imageKey + 'FileName',
          imageFileName = content[imageFileNameProperty];

    this.upload.deleteFile(`${this.tenant.id}/content/home/${imageFileName}`)
      .pipe(
        map(() => {

          const newContent: HomePageContent = deepClone(content);

          delete newContent[imageUrlProperty];
          delete newContent[imageFileNameProperty];

          return newContent;
        }),
        tap(content => this.store.dispatch(homePageContentUpdated({content}))),
        tap(() => this.messages.info("Image deleted."))
      )
      .subscribe();

  }


  save(content:HomePageContent) {

    const newContent: HomePageContent = deepClone(content);

    newContent.pageTitle = this.form.value.title;
    newContent.pageTitleColor = this.titleColor;
    newContent.titleFontSize = this.form.value.titleFontSize || 30;

    this.store.dispatch(homePageContentUpdated({content:newContent}));

    this.close();

  }

  onTitleColorChanged(color) {
    this.titleColor = color;
  }

}
